import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, PhoneIcon, EmailIcon } from './icons';

/**
 * Standalone, static Hebrew (RTL) landing page for the "Technology Partner"
 * (fractional CTO) offering. Intentionally self-contained: it does NOT use the
 * shared Layout/Navbar/Footer, has no CMS/data fetching, and hardcodes all copy.
 *
 * NOTE: This deliberately deviates from the CLAUDE.md i18n rule (central
 * translations, no hardcoded strings) because the page is required to be a
 * static, hardcoded, Hebrew-only marketing landing page with no CMS.
 */

// --- Constants ---------------------------------------------------------------

const SITE_URL = 'https://ronnieprojects.com';
const PAGE_URL = `${SITE_URL}/he/tech-partner`;
const LOGO_URL = '/rp_logo.webp';
const HEADSHOT_URL = '/michael-headshot.webp';

const PHONE_DISPLAY = '+972 74 760 2660';
const PHONE_TEL = '+972747602660';
const EMAIL = 'info@ronnieprojects.com';

const WHATSAPP_TEXT = 'היי, אשמח לשמוע על שירות השותף הטכנולוגי';
const WHATSAPP_URL = `https://wa.me/972747602660?text=${encodeURIComponent(WHATSAPP_TEXT)}`;

const PAGE_TITLE = 'שותף טכנולוגי לעסק שלך | Ronnie Projects';
const PAGE_DESCRIPTION =
  'שותף טכנולוגי בכיר לעסק שלך — מנהל טכנולוגיה במיקור חוץ לחברות עם ERP (Priority/SAP), חנות אונליין ותוכניות צמיחה, בלי משכורת של CTO. אסטרטגיה, פיקוח על ספקים והחלטות נכונות.';

// --- Content -----------------------------------------------------------------

const PAINS: { title: string; desc: string }[] = [
  {
    title: 'מפתחים שהבטיחו ונעלמו',
    desc: 'התחלתם פרויקט, שילמתם — והוא נתקע. אין אף אחד מהצד שלכם שבודק אותם ודואג שהעבודה באמת נעשית.',
  },
  {
    title: 'משלמים על מחשוב בלי להבין על מה',
    desc: 'כל חודש יוצא כסף על תוכנה, שרתים ותמיכה. אם תשאלו את עצמכם בכנות — אתם לא באמת יודעים על מה אתם משלמים.',
  },
  {
    title: 'כולם אומרים לכם "חייבים AI"',
    desc: 'כל אחד מסביר שאתם חייבים בינה מלאכותית. אף אחד לא מצליח להגיד בשביל מה בדיוק, ומה ההחזר על ההשקעה.',
  },
  {
    title: 'כל החלטה טכנולוגית מרגישה כמו הימור',
    desc: 'מערכת חדשה, אינטגרציה או גיוס איש טכנולוגיה — ואין את מי לשאול שלא מנסה למכור לכם משהו באותו הרגע.',
  },
  {
    title: 'עבודה ידנית בכל פינה',
    desc: 'הצוות מעתיק נתונים בין מערכות שוב ושוב. אתם יודעים שזה לא תקין — אבל לא יודעים מאיפה בכלל מתחילים.',
  },
];

const BENEFITS: { title: string; desc: string }[] = [
  {
    title: 'אסטרטגיה ומפת דרכים טכנולוגית',
    desc: 'תוכנית ברורה שמחוברת ליעדים העסקיים שלכם — לא רשימת קניות של טכנולוגיות.',
  },
  {
    title: 'פיקוח על ספקים וצוותי פיתוח',
    desc: 'מישהו מקצועי מהצד שלכם שבודק הצעות, לוחות זמנים ואיכות, ומוודא שאתם מקבלים תמורה.',
  },
  {
    title: 'החלטות "לפתח או לקנות" ובחירת מערכות',
    desc: 'מתי לבנות פתרון מותאם, מתי לקנות מהמדף, ואיזו מערכת באמת מתאימה לעסק שלכם.',
  },
  {
    title: 'הערכת AI כנה',
    desc: 'איפה בינה מלאכותית באמת עוזרת, ואיפה אוטומציה פשוטה תעשה את העבודה בשבריר מהעלות.',
  },
  {
    title: 'ארכיטקטורה ותשתית נתונים',
    desc: 'בסיס נכון ל-ERP, לחנות האונליין ולאינטגרציות — כדי שהמערכות ידברו ביניהן.',
  },
  {
    title: 'כתובת בכירה אחת לכל שאלה טכנולוגית',
    desc: 'במקום לרדוף אחרי כמה ספקים, יש לכם איש אחד שאחראי ומכיר את התמונה המלאה.',
  },
];

const CLIENTS = ['Skechers', 'Reebok', 'Replay', 'JD Sports'];

const STEPS: { title: string; desc: string }[] = [
  {
    title: 'שיחת היכרות',
    desc: 'נכיר את העסק, את האתגרים ואת מה שחשוב לכם. בלי התחייבות ובלי מכירה.',
  },
  {
    title: 'אבחון טכנולוגי',
    desc: 'אמפה את המערכות, הספקים, העלויות והסיכונים — ואחזור אליכם עם המלצות ברורות ומעשיות.',
  },
  {
    title: 'שותפות שוטפת',
    desc: 'התקשרות חודשית קבועה: אני נכנס לדיוני ההנהלה, מפקח על הביצוע, ומחזיק איתכם את מפת הדרכים הטכנולוגית.',
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: 'במה זה שונה מלהעסיק CTO?',
    a: 'בשבריר מהעלות של מנהל טכנולוגיות שכיר (מה שנקרא בעולם Fractional CTO). אתם מקבלים בכיר עם ניסיון מהיום הראשון — בלי סיכוני גיוס, בלי עלויות מעביד ובלי לחכות חודשים עד שמישהו נכנס לתפקיד.',
  },
  {
    q: 'במה זה שונה מיועץ?',
    a: 'יועץ נותן דוח והולך. אני נשאר. זו בעלות ואחריות שוטפת על ההחלטות ועל הביצוע — לא מסמך שנשכח במגירה.',
  },
  {
    q: 'אתה פשוט תדחוף לי את חברת הפיתוח שלך?',
    a: 'לא. שקיפות מלאה — ההחלטות תמיד שלכם, ואני מביא חלופות. אם במקרה מסוים Ronnie Projects היא באמת ההתאמה הנכונה, אגיד את זה בגלוי, ותמיד תוכלו לבחור אחרת.',
  },
  {
    q: 'כמה זמן אתה מקדיש לנו?',
    a: 'נוכחות שבועית קבועה. את ההיקף המדויק נסגור יחד בשיחת ההיכרות, לפי הצרכים והקצב של העסק.',
  },
  {
    q: 'כמה זה עולה?',
    a: 'תלוי בהיקף. נדבר על מספרים בפתיחות כבר בשיחה הראשונה — בלי לחץ ובלי התחייבות.',
  },
];

const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'שותף טכנולוגי / מנהל טכנולוגיה במיקור חוץ (Fractional CTO)',
  name: 'שותף טכנולוגי לעסק',
  description: PAGE_DESCRIPTION,
  url: PAGE_URL,
  areaServed: { '@type': 'Country', name: 'ישראל' },
  audience: {
    '@type': 'BusinessAudience',
    name: 'עסקים בינוניים בישראל — קמעונאות, מסחר אלקטרוני, יבואנים ומפיצים',
  },
  provider: {
    '@type': 'Organization',
    name: 'Ronnie Projects',
    url: SITE_URL,
    logo: `${SITE_URL}/rp_logo.png`,
    telephone: '+972-74-760-2660',
    email: EMAIL,
    founder: { '@type': 'Person', name: 'Michael Offengenden' },
  },
};

// --- Small building blocks ---------------------------------------------------

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

interface WhatsAppButtonProps {
  className?: string;
  label?: string;
}

/** Primary CTA — opens WhatsApp with the pre-filled message. */
function WhatsAppButton({ className = '', label = 'דברו איתי ב-WhatsApp' }: WhatsAppButtonProps) {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} (נפתח בחלון חדש)`}
      className={`group inline-flex items-center justify-center gap-3 rounded-full bg-green-500 px-8 py-4 text-lg font-bold text-white shadow-2xl shadow-green-500/30 transition-all duration-300 hover:scale-105 hover:bg-green-600 hover:shadow-green-500/50 ${className}`}
    >
      <WhatsAppIcon className="h-6 w-6" />
      <span>{label}</span>
    </a>
  );
}

// --- Page --------------------------------------------------------------------

export default function TechPartner() {
  // Vite dev server does no meta injection, so set RTL/lang/title on the client.
  // In production these are already set correctly by the SSR meta injection.
  useEffect(() => {
    const html = document.documentElement;
    const prevDir = html.dir;
    const prevLang = html.lang;
    const prevTitle = document.title;

    html.dir = 'rtl';
    html.lang = 'he';
    document.title = PAGE_TITLE;

    return () => {
      html.dir = prevDir;
      html.lang = prevLang;
      document.title = prevTitle;
    };
  }, []);

  return (
    <div dir="rtl" lang="he" className="font-heebo min-h-screen bg-white text-slate-900">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JSON_LD) }} />

      {/* Minimal header — logo only */}
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-6xl items-center px-5 py-5 sm:px-8">
          <Link to="/" aria-label="Ronnie Projects — לעמוד הבית">
            <img
              src={LOGO_URL}
              alt="Ronnie Projects"
              width={488}
              height={476}
              className="h-9 w-auto brightness-0 invert md:h-10"
            />
          </Link>
        </div>
      </header>

      <main id="main-content">
        {/* 1. HERO */}
        <section className="relative flex min-h-[92vh] items-center overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700">
          <div className="absolute inset-0 opacity-50" aria-hidden="true">
            <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(ellipse_at_top_right,_rgba(56,189,248,0.35)_0%,_transparent_50%)]" />
            <div className="absolute bottom-0 left-0 h-full w-full bg-[radial-gradient(ellipse_at_bottom_left,_rgba(168,85,247,0.35)_0%,_transparent_55%)]" />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-5xl px-5 pb-20 pt-32 text-center sm:px-8">
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-2 text-sm font-semibold text-white backdrop-blur-md">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-400" />
              </span>
              שותף טכנולוגי לעסקים כמו שלך
            </span>

            <h1 className="mx-auto mb-8 max-w-4xl text-4xl font-black leading-tight text-white drop-shadow-lg sm:text-5xl lg:text-6xl">
              מנהל הטכנולוגיה של העסק שלך —{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-l from-yellow-200 via-yellow-300 to-amber-400">
                בלי משכורת של 60 אלף בחודש
              </span>
            </h1>

            <p className="mx-auto mb-10 max-w-3xl text-lg leading-relaxed text-white/90 sm:text-xl">
              לחברות עם פעילות אמיתית — מערכת ERP, חנות אונליין ותוכניות צמיחה — אבל בלי
              CTO. שותף בכיר אחד שמתרגם צרכים עסקיים להחלטות טכנולוגיות נכונות.
            </p>

            <WhatsAppButton className="w-full sm:w-auto" />
          </div>
        </section>

        {/* 2. PAIN — "נשמע מוכר?" */}
        <section className="relative overflow-hidden bg-slate-950 py-20 sm:py-28">
          <div className="absolute inset-0" aria-hidden="true">
            <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(ellipse_at_top_left,_rgba(239,68,68,0.12)_0%,_transparent_50%)]" />
            <div className="absolute right-0 top-0 h-full w-full bg-[radial-gradient(ellipse_at_top_right,_rgba(251,146,60,0.12)_0%,_transparent_50%)]" />
          </div>

          <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
            <h2 className="mb-4 text-center text-4xl font-black text-white sm:text-5xl">
              <span className="text-transparent bg-clip-text bg-gradient-to-l from-red-400 via-orange-400 to-amber-400">
                נשמע
              </span>{' '}
              מוכר?
            </h2>
            <p className="mx-auto mb-14 max-w-2xl text-center text-lg text-white/50">
              אם אחד מאלה מדבר אליכם — כנראה חסר לכם מישהו טכנולוגי מהצד שלכם.
            </p>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {PAINS.map((pain, idx) => (
                <div
                  key={idx}
                  className="group relative rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-all duration-300 hover:border-white/20 hover:bg-white/[0.06]"
                >
                  <div
                    className="absolute inset-x-8 top-0 h-1 rounded-b-full bg-gradient-to-l from-red-500 to-amber-500 opacity-60 transition-opacity group-hover:opacity-100"
                    aria-hidden="true"
                  />
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-amber-500 text-xl font-black text-white shadow-lg">
                    {idx + 1}
                  </div>
                  <h3 className="mb-3 text-xl font-bold leading-tight text-white">{pain.title}</h3>
                  <p className="leading-relaxed text-white/50">{pain.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-14 text-center">
              <WhatsAppButton label="בואו נדבר על זה ב-WhatsApp" className="w-full sm:w-auto" />
            </div>
          </div>
        </section>

        {/* 3. WHAT YOU GET — "מה מקבלים" */}
        <section className="bg-slate-50 py-20 sm:py-28">
          <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <div className="mb-14 text-center">
              <span className="mb-4 inline-block text-sm font-bold uppercase tracking-[0.25em] text-[#4f6cff]">
                מה מקבלים
              </span>
              <h2 className="text-3xl font-black text-slate-900 sm:text-4xl lg:text-5xl">
                שותף אחד. תמונה מלאה.
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {BENEFITS.map((benefit, idx) => (
                <div
                  key={idx}
                  className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#4f6cff]/10"
                >
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#4f6cff]/10 text-[#4f6cff]">
                    <CheckIcon className="h-6 w-6" strokeWidth={2.5} />
                  </div>
                  <h3 className="mb-3 text-lg font-bold text-slate-900">{benefit.title}</h3>
                  <p className="leading-relaxed text-slate-500">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. WHO AM I — "מי אני" */}
        <section className="bg-white py-20 sm:py-28">
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[320px_1fr]">
              <div className="mx-auto w-full max-w-xs">
                <div className="relative overflow-hidden rounded-3xl border-4 border-white shadow-2xl ring-1 ring-slate-100">
                  <img
                    src={HEADSHOT_URL}
                    alt="מיכאל אופנגנדן, מייסד Ronnie Projects"
                    width={640}
                    height={800}
                    loading="lazy"
                    decoding="async"
                    className="h-auto w-full object-cover"
                  />
                </div>
              </div>

              <div>
                <span className="mb-4 inline-block text-sm font-bold uppercase tracking-[0.25em] text-[#4f6cff]">
                  מי אני
                </span>
                <h2 className="mb-6 text-3xl font-black text-slate-900 sm:text-4xl">מיכאל אופנגנדן</h2>
                <div className="space-y-4 text-lg leading-relaxed text-slate-600">
                  <p>
                    20+ שנה שאני בונה תוכנה ואינטגרציות לעסקים בישראל. מעל 150 פרויקטים, בין היתר עם
                    מותגים מובילים כמו Skechers, Reebok, Replay ו-JD Sports.
                  </p>
                  <p>
                    לאורך הדרך ראיתי בדיוק איפה עסקים מפסידים כסף בגלל החלטות טכנולוגיות שגויות — ואיפה
                    הטכנולוגיה הנכונה עושה את ההבדל בין עסק שרץ במקום לעסק שצומח.
                  </p>
                </div>

                <blockquote className="mt-8 border-r-4 border-[#4f6cff] pr-5 text-xl font-bold text-slate-900">
                  "אני בונה דברים שעובדים, ועוזר לעסקים לצמוח."
                </blockquote>

                <ul className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
                  {CLIENTS.map((client) => (
                    <li
                      key={client}
                      lang="en"
                      dir="ltr"
                      className="text-lg font-bold tracking-wide text-slate-400"
                    >
                      {client}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5. HOW IT WORKS — "איך זה עובד" */}
        <section className="relative overflow-hidden bg-[#0a0a1a] py-20 sm:py-28">
          <div className="absolute inset-0 bg-gradient-to-b from-[#4f6cff]/5 via-transparent to-[#7c3aed]/5" aria-hidden="true" />

          <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
            <div className="mb-16 text-center">
              <span className="mb-4 inline-block text-sm font-bold uppercase tracking-[0.25em] text-[#4f6cff]">
                איך זה עובד
              </span>
              <h2 className="text-3xl font-black text-white sm:text-4xl lg:text-5xl">
                שלושה צעדים פשוטים
              </h2>
            </div>

            <ol className="grid gap-8 md:grid-cols-3">
              {STEPS.map((step, idx) => (
                <li
                  key={idx}
                  className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-8 pt-12"
                >
                  <div className="absolute -top-6 right-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4f6cff] to-[#7c3aed] text-2xl font-black text-white shadow-lg shadow-[#4f6cff]/40">
                    {idx + 1}
                  </div>
                  <h3 className="mb-3 text-xl font-black leading-tight text-white">{step.title}</h3>
                  <p className="leading-relaxed text-white/50">{step.desc}</p>
                </li>
              ))}
            </ol>

            <div className="mt-16 text-center">
              <WhatsAppButton label="קבעו שיחת היכרות ב-WhatsApp" className="w-full sm:w-auto" />
            </div>
          </div>
        </section>

        {/* 6. FAQ */}
        <section className="bg-slate-50 py-20 sm:py-28">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <h2 className="mb-12 text-center text-3xl font-black text-slate-900 sm:text-4xl">
              שאלות נפוצות
            </h2>

            <div className="space-y-4">
              {FAQS.map((faq, idx) => (
                <details
                  key={idx}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 [&_svg]:open:rotate-45"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold text-slate-900 marker:content-['']">
                    <span>{faq.q}</span>
                    <svg
                      className="h-6 w-6 flex-shrink-0 text-[#4f6cff] transition-transform duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </summary>
                  <p className="mt-4 leading-relaxed text-slate-600">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* 7. FINAL CTA */}
        <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 py-20 sm:py-28">
          <div className="absolute inset-0 opacity-40" aria-hidden="true">
            <div className="absolute right-0 top-0 h-full w-full bg-[radial-gradient(ellipse_at_top_right,_rgba(56,189,248,0.35)_0%,_transparent_55%)]" />
          </div>

          <div className="relative z-10 mx-auto max-w-3xl px-5 text-center sm:px-8">
            <h2 className="mb-5 text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl">
              שיחה אחת כנה על הטכנולוגיה של העסק שלכם
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-white/90">
              בלי התחייבות ובלי מכירה. נבין ביחד איפה אתם היום ואיפה הטכנולוגיה יכולה לקחת אתכם.
            </p>

            <WhatsAppButton className="w-full sm:w-auto" />

            <div className="mt-8 flex flex-col items-center justify-center gap-4 text-white/90 sm:flex-row sm:gap-8">
              <a
                href={`tel:${PHONE_TEL}`}
                className="inline-flex items-center gap-2 font-semibold transition-colors hover:text-white"
              >
                <PhoneIcon className="h-5 w-5" />
                <span dir="ltr">{PHONE_DISPLAY}</span>
              </a>
              <a
                href={`mailto:${EMAIL}`}
                className="inline-flex items-center gap-2 font-semibold transition-colors hover:text-white"
              >
                <EmailIcon className="h-5 w-5" />
                <span dir="ltr">{EMAIL}</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Minimal footer */}
      <footer className="bg-slate-900 text-slate-400" role="contentinfo">
        <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
          <div className="flex flex-col items-center gap-8 text-center md:flex-row md:justify-between md:text-right">
            <img
              src={LOGO_URL}
              alt="Ronnie Projects"
              width={488}
              height={476}
              loading="lazy"
              className="h-8 w-auto brightness-0 invert"
            />

            <ul className="flex flex-col items-center gap-4 sm:flex-row sm:gap-8">
              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 transition-colors hover:text-white"
                  aria-label="WhatsApp (נפתח בחלון חדש)"
                >
                  <WhatsAppIcon className="h-5 w-5 text-green-400" />
                  <span>WhatsApp</span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="inline-flex items-center gap-2 transition-colors hover:text-white"
                >
                  <PhoneIcon className="h-4 w-4 text-indigo-400" />
                  <span dir="ltr">{PHONE_DISPLAY}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${EMAIL}`}
                  className="inline-flex items-center gap-2 transition-colors hover:text-white"
                >
                  <EmailIcon className="h-4 w-4 text-indigo-400" />
                  <span dir="ltr">{EMAIL}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 text-sm md:flex-row">
            <p>
              © {new Date().getFullYear()} <span lang="en">Ronnie Projects</span>. כל הזכויות שמורות.
            </p>
            <div className="flex gap-6">
              <Link to="/he/privacy" className="transition-colors hover:text-white">
                מדיניות פרטיות
              </Link>
              <Link to="/he/terms" className="transition-colors hover:text-white">
                תנאי שימוש
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
